import React, { useEffect, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { ArrowForward, Assessment } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getSPDPreworkList } from '../../endpoints/annualReportsTs';

const Prework: React.FC = () => {
  const navigate = useNavigate();

  const { data: spdData, isLoading: spdLoading } = useApiGet(
    ['spd-prework-list-check'],
    () => getSPDPreworkList(),
    {
      enabled: true,
      retry: 1,
    }
  );

  const spdForms = useMemo(() => spdData?.forms || [], [spdData]);
  const totalPreworkItems = spdForms.length; // Will add more types later

  useEffect(() => {
    // If user has exactly one prework item total across all types, navigate directly to it
    if (totalPreworkItems === 1 && spdForms.length === 1) {
      navigate(`/annual-reports/prework/spd/${spdForms[0].id}`, {
        replace: true,
      });
    }
  }, [spdForms, totalPreworkItems, navigate]);

  // Show loading while checking
  if (spdLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If redirecting, show nothing
  if (totalPreworkItems === 1 && spdForms.length === 1) {
    return null;
  }

  // Show prework type selection
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Annual Reports Prework
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a prework category to begin
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* SPD Prework Card */}
        {spdForms.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => {
                if (spdForms.length === 1) {
                  navigate(`/annual-reports/prework/spd/${spdForms[0].id}`);
                } else {
                  navigate('/annual-reports/prework/spd');
                }
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Assessment color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                          SPD Prework
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Dues and assessments information for SPD chapters
                      </Typography>
                    </Box>
                    <ArrowForward color="action" />
                  </Stack>
                  <Box>
                    <Chip
                      label={`${spdForms.length} form${spdForms.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Future: Local Chapter Prework Card will go here */}
        {/* 
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Local Chapter Prework</Typography>
              <Typography variant="body2">Coming soon...</Typography>
            </CardContent>
          </Card>
        </Grid>
        */}

        {spdForms.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Assessment
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Prework Items Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You don't have any prework forms assigned at this time.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Prework;
